/**
 * *May an authorised person approve on this entity?* — the staff half of C8 §3b.
 * The kit's `Switch` since Wave 15B (C14 §10.6; T-6.14); it still sends only
 * itself and still renders from the server's value.
 *
 * Spec §10.1 question 3b, the owner, 2026-09-23; task **T-5.32**; Wave 9F.
 * The column is `prodesk_clients.authorised_may_approve`
 * (`0028_authorised_approval_switch`) and this is the only thing in the product
 * that can set it.
 *
 * ---
 *
 * ## Why this control is not a nicety
 *
 * The column defaults to **`false`**, which is the owner's answer and is the
 * right one: until 9F the portal enforced role on two routes and no more, so
 * every `authorised` person in the estate has in fact been approving
 * deliverables since the portal shipped — a power no firm was ever asked
 * about. C8 §3b would not preserve it.
 *
 * But that default **takes something away on migration day**, and a firm that
 * wanted the delegation finds out when somebody complains. This checkbox is the
 * entire answer to that cost: it is the only place a firm can discover the
 * switch exists, which is why waves.md put the column and the control in one
 * session rather than letting the column ship as a field with no screen.
 *
 * So it lives **under the list of linked people**, where the role names it is
 * about are already on the screen, and not inside the Edit dialog behind a
 * button — a setting nobody opens is a setting nobody finds.
 *
 * ## What governs it
 *
 * `client:can_update` — the verb that already governs editing this record. Not
 * a verb of its own: it is a fact about the client entity, a partner who may
 * change the client's PAN may change this, and a second permission over one
 * boolean is a second thing for a firm to get wrong. Without the verb the row
 * still renders, as a **sentence**: somebody who may read a client should be
 * able to see what their authorised people can do, and a control that vanishes
 * makes the state unknowable rather than unchangeable.
 *
 * ## The server is the authority
 *
 * The box renders from `client.authorised_may_approve` on every pass and keeps
 * no optimistic copy. A PATCH that failed and a box that stayed ticked is
 * exactly the disagreement between what a screen says and what is stored that
 * the whole of C8 §3 is about (CLAUDE.md §1a: nothing here invents a state).
 */

import { Alert, Switch } from '@fiveox/ui'
import { ShieldCheck } from 'lucide-react'
import { useState } from 'react'

import { CLIENT, useEvaluation } from '../../lib/access'
import { ApiError } from '../../lib/api'
import { type ClientRecord, type LinkedPerson, clients } from '../../lib/api/clients'

export function ApprovalSwitch({
  client,
  people,
  onChanged,
}: {
  client: ClientRecord
  people: LinkedPerson[]
  onChanged: () => void
}) {
  const { can } = useEvaluation()
  const mayEdit = can(CLIENT, 'can_update')

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const on = client.authorised_may_approve
  const authorised = people.filter((person) => person.role === 'authorised')

  const toggle = async (next: boolean) => {
    setSaving(true)
    setError(null)
    try {
      await clients.update(client.id, { authorised_may_approve: next })
      onChanged()
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : 'That could not be saved. Try again.',
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="border-border bg-muted/40 rounded-xl border p-4">
      <div className="flex items-start gap-3">
        <span className="bg-primary/10 text-primary flex h-8 w-8 shrink-0 items-center justify-center rounded-lg">
          <ShieldCheck size={16} aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          {mayEdit ? (
            <Switch
              checked={on}
              disabled={saving}
              onChange={(event) => void toggle(event.target.checked)}
              label="Authorised people can approve documents for this client"
            />
          ) : (
            <p className="text-foreground text-sm">
              Authorised people {on ? 'can' : 'cannot'} approve documents for this client
            </p>
          )}
          <p className="text-muted-foreground mt-1 text-xs">
            {/* The three sentences are three different situations and a firm in
                the third one needs to be told something different from a firm
                in the second. The middle sentence is the one that matters: it
                names, by count, the people this box is currently withholding
                something from. */}
            {on
              ? 'An owner of this client can always approve. Authorised people can too.'
              : authorised.length > 0
                ? `Only an owner can approve. ${authorised.length} authorised ${
                    authorised.length === 1 ? 'person' : 'people'
                  } on this client cannot.`
                : 'Only an owner can approve. Viewers never can.'}
          </p>
          {error && (
            <Alert tone="error" className="mt-2">
              {error}
            </Alert>
          )}
        </div>
      </div>
    </div>
  )
}
